import { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { AuditResult, AuditCriterion } from '@/types/plan';

function verdictColor(verdict: 'pass' | 'warn' | 'fail'): string {
  switch (verdict) {
    case 'pass': return FOREST_GREEN.DEFAULT;
    case 'warn': return WARNING.DEFAULT;
    case 'fail': return ERROR.DEFAULT;
  }
}

function verdictIcon(verdict: 'pass' | 'warn' | 'fail') {
  switch (verdict) {
    case 'pass': return CheckCircle2;
    case 'warn': return AlertTriangle;
    case 'fail': return XCircle;
  }
}

interface PlanAuditResultCardProps {
  auditResult: AuditResult;
  auditType: string;
  compact?: boolean;
  onImproveCriterion?: (criterionName: string) => void;
}

export function PlanAuditResultCard({ auditResult, auditType, compact, onImproveCriterion }: PlanAuditResultCardProps) {
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const color = verdictColor(auditResult.verdict);
  const Icon = verdictIcon(auditResult.verdict);

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12 }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span style={{ fontWeight: 600, color }}>{auditResult.overallScore}/100</span>
        <span style={{ color: MID_GRAY.DEFAULT, textTransform: 'uppercase', fontSize: 10, fontWeight: 600 }}>
          {auditResult.verdict}
        </span>
      </span>
    );
  }

  const toggleCriterion = (name: string) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        padding: 16,
      }}
    >
      <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
        <Icon className="w-4 h-4" style={{ color }} />
        <span style={{ fontWeight: 600, fontSize: 14, color: MID_GRAY.DEFAULT }}>{auditType}</span>
      </div>

      {/* Score header */}
      <div
        className="flex items-center justify-center gap-3"
        style={{
          padding: '12px 16px',
          borderRadius: 6,
          backgroundColor: `${color}08`,
          border: `1px solid ${color}20`,
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 28, fontWeight: 700, color }}>{auditResult.overallScore}</span>
        <span style={{ fontSize: 14, color: MID_GRAY.DEFAULT }}>/ 100</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            color: WHITE,
            backgroundColor: color,
            padding: '2px 8px',
            borderRadius: 4,
          }}
        >
          {auditResult.verdict}
        </span>
      </div>

      {/* Criteria breakdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {auditResult.criteria.map((criterion: AuditCriterion) => {
          const cColor = verdictColor(criterion.verdict);
          const CIcon = verdictIcon(criterion.verdict);
          const isExpanded = expandedCriteria.has(criterion.name);

          return (
            <div key={criterion.name}>
              <button
                type="button"
                className="flex items-center gap-2 w-full"
                onClick={() => toggleCriterion(criterion.name)}
                aria-expanded={isExpanded}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', textAlign: 'left' }}
              >
                <CIcon className="w-4 h-4 flex-shrink-0" style={{ color: cColor }} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{criterion.name}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: cColor, fontVariantNumeric: 'tabular-nums' }}>
                  {criterion.score}/100
                </span>
                {isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5" style={{ color: MID_GRAY.DEFAULT }} />
                  : <ChevronRight className="w-3.5 h-3.5" style={{ color: MID_GRAY.DEFAULT }} />}
              </button>
              {onImproveCriterion && criterion.verdict !== 'pass' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onImproveCriterion(criterion.name); }}
                  style={{
                    background: 'none',
                    border: `1px solid ${cColor}40`,
                    borderRadius: 4,
                    padding: '1px 6px',
                    fontSize: 11,
                    fontWeight: 500,
                    color: cColor,
                    cursor: 'pointer',
                    marginLeft: 4,
                    flexShrink: 0,
                  }}
                >
                  Improve
                </button>
              )}
              {isExpanded && (criterion.feedback?.length || criterion.reasoning) && (
                <ul style={{ margin: '4px 0 4px 24px', padding: '0 0 0 16px', fontSize: 12, listStyleType: 'disc' }}>
                  {(criterion.feedback && criterion.feedback.length > 0
                    ? criterion.feedback
                    : (criterion.reasoning?.split(/(?<=[.!?])\s+/).filter(s => s.trim()) ?? []).map(s => ({ text: s, sentiment: 'neutral' as const }))
                  ).map((item, idx) => (
                    <li key={idx} style={{
                      color: item.sentiment === 'positive' ? MID_GRAY.DEFAULT
                        : item.sentiment === 'negative' ? WARNING.DEFAULT
                        : MID_GRAY.lighter,
                      lineHeight: 1.5, marginBottom: 2,
                    }}>{item.text}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Issues */}
      {auditResult.blockingIssues.length > 0 && (
        <div style={{ background: ERROR.bg, borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>Blocking Issues: {auditResult.blockingIssues.length}</span>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {auditResult.blockingIssues.map((issue, i) => (
              <li key={i} style={{ color: ERROR.DEFAULT, lineHeight: 1.5 }}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {auditResult.warnings.length > 0 && (
        <div style={{ background: `${WARNING.DEFAULT}15`, borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>Warnings: {auditResult.warnings.length}</span>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {auditResult.warnings.map((warn, i) => (
              <li key={i} style={{ color: WARNING.DEFAULT, lineHeight: 1.5 }}>{warn}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions */}
      {auditResult.suggestions.length > 0 && (
        <div>
          <button
            type="button"
            className="flex items-center gap-1"
            onClick={() => setShowSuggestions(!showSuggestions)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: MID_GRAY.DEFAULT, padding: '4px 0' }}
          >
            Suggestions: {auditResult.suggestions.length}
            {showSuggestions
              ? <ChevronDown className="w-3 h-3" />
              : <ChevronRight className="w-3 h-3" />}
          </button>
          {showSuggestions && (
            <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
              {auditResult.suggestions.map((s, i) => (
                <li key={i} style={{ color: MID_GRAY.DEFAULT, lineHeight: 1.5 }}>{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
